import React, { ReactNode } from "react";
import UnauthorizedPage from "@/pages/unauthorized/unauthorized-page";
import { useSession, SessionProvider } from "@/providers/session-provider";
import AuthLoading from "@/common/components/AuthLoading/auth-loading";
import { Route, RouteProps } from "react-router-dom";

/**
 * Props for the {@link ProtectedRoute} component.
 */
interface ProtectedRouteProps extends RouteProps {
  /** The permission string the current user must have to access this content. */
  requiredPermission: string;
  /** The route content to render when the user is authorized. */
  children: ReactNode;
  /** Rendered by the internal {@link AuthLoading} while authorization is pending. */
  loadingSkeleton: ReactNode;
  /**
   * When set, unauthorized users are redirected to this URL instead of seeing
   * the {@link UnauthorizedPage}. Used to replicate pre-split behavior where
   * users without the hardware shop permission were sent back to IT Shop.
   */
  redirectUrl?: string;
}

interface PermissionGateProps {
  requiredPermission: string;
  children: ReactNode;
  redirectUrl?: string;
}

const PermissionGate: React.FC<PermissionGateProps> = ({ requiredPermission, children, redirectUrl }) => {
  const { hasPermission } = useSession();

  if (!hasPermission(requiredPermission)) {
    if (redirectUrl) {
      window.location.assign(redirectUrl);
      return null;
    }
    return <UnauthorizedPage />;
  }

  return <>{children}</>;
};

/**
 * Owns the full auth stack for a route: `Route` (so react-router's `Switch`
 * can match on `path`/`exact` directly) wrapping `AuthLoading` (resolves
 * Midway auth, showing `loadingSkeleton` while pending) wrapping
 * `SessionProvider` (exposes permissions) wrapping a permission check that
 * renders `children` when authorized or {@link UnauthorizedPage} otherwise.
 *
 * When `redirectUrl` is provided, unauthorized users are redirected to that
 * external URL (e.g. IT Shop home) instead of seeing the unauthorized page.
 *
 * Each `ProtectedRoute` runs its own `AuthLoading`/`SessionProvider`, so
 * different routes can show different loading skeletons (e.g. the shop's
 * full header skeleton vs. a header-less skeleton for `/campaigns`).
 *
 * @example
 * ```tsx
 * <ProtectedRoute
 *   path="/admin"
 *   requiredPermission="admin:access"
 *   redirectUrl="https://shop.it.a2z.com"
 * >
 *   <AdminDashboard />
 * </ProtectedRoute>
 * ```
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  requiredPermission,
  children,
  loadingSkeleton,
  redirectUrl,
  ...routeProps
}) => {
  return (
    <Route {...routeProps}>
      <AuthLoading loadingSkeleton={loadingSkeleton}>
        <SessionProvider>
          <PermissionGate requiredPermission={requiredPermission} redirectUrl={redirectUrl}>
            {children}
          </PermissionGate>
        </SessionProvider>
      </AuthLoading>
    </Route>
  );
};

export default ProtectedRoute;
